import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { AlertGroup, AlertSettingKey, IAlertSettingRow, IAlertSettingUpdate, IRestrictedArea } from '@d-orca/shared/alert-kinds';
import { selectSiteId } from '@store/selectors/site.selectors';
import { useCanAccess } from '@permissions/useFeatures';
import { DataUnavailable } from '@components/DataUnavailable/DataUnavailable';
import { AlertSettingsList } from '@components/AlertSettings/AlertSettingsList';
import { AlertSettingsLoading } from '@components/AlertSettings/AlertSettingsLoading';
import { rowChangeHandlers } from '@components/AlertSettings/rowChangeHandlers';
import { Button } from '@components/ui/Button/Button';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { PlusIcon } from '@utils/icons';
import { useAdminUsers } from '../UsersTab/hooks/useAdminUsers';
import {
  useAlertsHub,
  useCreateRestrictedArea,
  useDeleteRestrictedArea,
  useRestrictedAreaOptions,
  useUpdateAlertsHub,
  useUpdateRestrictedArea,
} from './hooks/useAlertsHub';
import { eligibleRecipients, visibleRecipients } from './recipientRules';
import { RecipientsPick } from './RecipientsPick';
import { ConfirmReachDialog } from './ConfirmReachDialog';
import { RestrictedAreaDialog } from './RestrictedAreaDialog/RestrictedAreaDialog';
import { PageHeader } from '@components/ui/PageHeader/PageHeader';
import adminStyles from '../AdminPage.module.css';
import styles from './AlertsHubTab.module.css';

/** A channel or frequency change waiting on the reach confirmation. */
interface IPendingChange {
  key: AlertSettingKey;
  update: IAlertSettingUpdate;
  reach: number;
}

/** The restricted-area dialog's subject: `area: null` creates one. */
interface IEditingArea {
  area: IRestrictedArea | null;
}

/** Admin → Alerts Hub: who gets each alert in this project, and on which channels. */
export function AlertsHubTab() {
  const siteId = useSelector(selectSiteId);
  return siteId ? <AlertsHub siteId={siteId} /> : null;
}

function AlertsHub({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const hub = useAlertsHub(siteId);
  const roster = useAdminUsers(siteId);
  const viewerIsTrusstorAdmin = useCanAccess('admin.trusstor');
  const save = useUpdateAlertsHub(siteId);
  const hasSafety = !!hub.data?.groups.some((g) => g.group === 'safety');
  const areaOptions = useRestrictedAreaOptions(siteId, hasSafety);
  const createArea = useCreateRestrictedArea(siteId);
  const updateArea = useUpdateRestrictedArea(siteId);
  const deleteArea = useDeleteRestrictedArea(siteId);
  const [pending, setPending] = useState<IPendingChange | null>(null);
  const [editing, setEditing] = useState<IEditingArea | null>(null);

  const contractors = new Map(
    (areaOptions.data?.professions ?? []).flatMap((p) => (p.contractor ? [[p.id, p.contractor] as const] : [])),
  );
  const closeDialog = () => setEditing(null);

  const safetyEligible = new Set(hub.data?.safetyEligible ?? []);
  const rowProps = (row: IAlertSettingRow, group: AlertGroup) => {
    const eligible = eligibleRecipients(roster.data!.users, group, viewerIsTrusstorAdmin, safetyEligible);
    // Every Hub row carries its recipients (none yet: an empty list).
    const recipients = visibleRecipients(row.recipients!, eligible);
    // The change overwrites the choice of EVERY recipient, listed to this
    // viewer or not, so that is who the confirmation counts.
    const reach = row.recipients!.length;
    const area = row.restrictedArea;
    return {
      // A change on a row with recipients overwrites their own choice — ask first.
      ...rowChangeHandlers((update) =>
        reach > 0 ? setPending({ key: row.key, update, reach }) : save.mutate({ key: row.key, update }),
      ),
      recipients: (
        <RecipientsPick
          alertName={t(`alertSettings.kinds.${row.kind}.name`)}
          eligible={eligible}
          recipients={recipients}
          onToggle={(username, add) =>
            save.mutate({ key: row.key, update: { recipients: add ? { add: [username] } : { remove: [username] } } })
          }
        />
      ),
      onEditArea: area ? () => setEditing({ area }) : undefined,
      contractors,
    };
  };

  const groupAction = (group: AlertGroup) =>
    group === 'safety' && (
      <Button variant="secondary" size="sm" disabled={!areaOptions.data} onClick={() => setEditing({ area: null })}>
        <PlusIcon size={14} />
        {t('alertSettings.restrictedArea.create')}
      </Button>
    );

  return (
    <div className={adminStyles.page}>
      <div className={adminStyles.pageHeader}>
        <PageHeader title={t('admin.tabs.alertsHub')} subtitle={t('alertSettings.hub.intro')} />
      </div>

      <ScrollArea className={styles.body} viewportClassName={styles.bodyInner}>
        {hub.isError || roster.isError ? (
          <DataUnavailable />
        ) : !hub.data || !roster.data ? (
          <AlertSettingsLoading />
        ) : (
          <AlertSettingsList groups={hub.data.groups} rowProps={rowProps} groupAction={groupAction} />
        )}
      </ScrollArea>

      {pending && (
        <ConfirmReachDialog
          reach={pending.reach}
          onCancel={() => setPending(null)}
          onConfirm={() => {
            save.mutate({ key: pending.key, update: pending.update });
            setPending(null);
          }}
        />
      )}

      {editing && areaOptions.data && (
        <RestrictedAreaDialog
          area={editing.area}
          options={areaOptions.data}
          submitting={createArea.isPending || updateArea.isPending || deleteArea.isPending}
          onClose={closeDialog}
          onSubmit={(input) =>
            editing.area
              ? updateArea.mutate({ id: editing.area.id, input }, { onSuccess: closeDialog })
              : createArea.mutate(input, { onSuccess: closeDialog })
          }
          onDelete={() => deleteArea.mutate(editing.area!.id, { onSuccess: closeDialog })}
        />
      )}
    </div>
  );
}
